/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { call, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Asset,
  AssetBookmark,
  BookmarkUtils,
  PlayableAsset,
  Ducks,
  MenuItem,
  AssetUtils,
  TimeUtils,
  Episode,
} from '@wit/vtv-x-foundation-lib';
import { SeasonsData } from '../seasons';

const MAX_PROGRESS = 95;
const MIN_PROGRESS = 0;

type BookmarksCache = Ducks.BookmarksCache.ReducerState;

function getAssetBookmark(bookmarksCache: BookmarksCache, asset: Asset): AssetBookmark | undefined {
  const id = BookmarkUtils.getBookmarkAssetId(asset);
  return bookmarksCache[id]?.data;
}

function getProgress(bookmarksCache: BookmarksCache, episode: PlayableAsset): number {
  const bookmark: AssetBookmark | undefined = getAssetBookmark(bookmarksCache, episode);

  if (bookmark) {
    return AssetUtils.getProgressInPercentage(bookmark.position, TimeUtils.minutesToSeconds(episode.durationValue));
  }

  return 0;
}

function getNextEpisode(seasonsData: SeasonsData, curEpisode: Asset): Asset | undefined {
  const episodesBySeason = Object.values(seasonsData.episodesBySeason);

  let curEpisodeIndex: number;
  let episodes: Asset[];

  for (let i = 0; i < episodesBySeason.length; i += 1) {
    episodes = episodesBySeason[i];

    curEpisodeIndex = episodes.findIndex(
      (episode) => (episode as Episode).internalId === (curEpisode as Episode).internalId
    );

    if (curEpisodeIndex > -1) {
      if (curEpisodeIndex !== episodes.length - 1) {
        return episodes[curEpisodeIndex + 1]; // next episode from current season
      }

      return episodesBySeason[i + 1]?.[0]; // first episode from next season
    }
  }
  return undefined;
}

function getLowestEpisodeFromLowestSeason(seasonsData: SeasonsData): Asset {
  const episodesBySeason = Object.values(seasonsData.episodesBySeason);
  return episodesBySeason[0][0];
}

function* getBestEpisode(
  seasonsData: SeasonsData,
  entryAsset: Asset,
  entryMenuItem: MenuItem
): SagaIterator<Asset | undefined> {
  const bookmarksCache: BookmarksCache = yield select(Ducks.BookmarksCache.Selectors.getBookmarks);

  const recommBestEpisode: Asset | undefined = yield call(
    Ducks.Recommendations.getBestEpisode,
    entryMenuItem,
    entryAsset
  );

  if (recommBestEpisode && AssetUtils.isPlayableAsset(recommBestEpisode)) {
    const progress = getProgress(bookmarksCache, recommBestEpisode);

    if (progress >= MAX_PROGRESS) {
      const nextEpisode = getNextEpisode(seasonsData, recommBestEpisode);
      // TODO: entitlements validation
      if (nextEpisode) {
        return nextEpisode;
      }
    } else if (progress > MIN_PROGRESS) {
      return recommBestEpisode;
    }

    // no bookmark found or equals to 0
    return getLowestEpisodeFromLowestSeason(seasonsData);
  }

  return entryAsset;
}

export default {
  getBestEpisode,
};
